import type {
  EmployeeDocumentCategory,
  EmployeeEmploymentStatus,
  EmployeeEmploymentType,
  EmployeeGoalStatus,
  EmployeePayReason,
  EmployeeTimelineKind,
} from '@/types/database';

export const EMPLOYMENT_TYPES: { value: EmployeeEmploymentType; label: string }[] = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'intern', label: 'Intern' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'temporary', label: 'Temporary' },
];

export const EMPLOYMENT_STATUSES: { value: EmployeeEmploymentStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'on_leave', label: 'On leave' },
  { value: 'terminated', label: 'Former' },
];

export const PAY_REASONS: { value: EmployeePayReason; label: string }[] = [
  { value: 'hire', label: 'Starting rate' },
  { value: 'raise', label: 'Raise' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'correction', label: 'Correction' },
];

export const GOAL_STATUSES: { value: EmployeeGoalStatus; label: string }[] = [
  { value: 'active', label: 'In progress' },
  { value: 'achieved', label: 'Achieved' },
  { value: 'paused', label: 'Paused' },
];

export const DOCUMENT_CATEGORIES: { value: EmployeeDocumentCategory; label: string }[] = [
  { value: 'offer_letter', label: 'Offer letter' },
  { value: 'i9', label: 'I-9' },
  { value: 'tax', label: 'Tax forms' },
  { value: 'agreement', label: 'Signed agreement' },
  { value: 'certification', label: 'Certification' },
  { value: 'direct_deposit', label: 'Direct deposit' },
  { value: 'handbook', label: 'Handbook acknowledgment' },
  { value: 'other', label: 'Other' },
];

export const TIMELINE_KINDS: { value: EmployeeTimelineKind; label: string }[] = [
  { value: 'hired', label: 'Hired' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'raise', label: 'Raise' },
  { value: 'recognition', label: 'Recognition' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'leave', label: 'Leave' },
  { value: 'note', label: 'Note' },
  { value: 'document', label: 'Document' },
];

export const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'] as const;

export const EMPLOYEE_DOCUMENTS_BUCKET = 'employee-documents';
export const EMPLOYEE_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;

export function labelOf<T extends string>(
  options: { value: T; label: string }[],
  value: T | null | undefined,
): string {
  if (!value) return '—';
  return options.find((o) => o.value === value)?.label ?? value;
}

/** Map a pay-change reason onto the timeline kind that should record it. */
export function timelineKindForPayReason(reason: EmployeePayReason): EmployeeTimelineKind {
  if (reason === 'hire') return 'hired';
  if (reason === 'raise') return 'raise';
  if (reason === 'promotion') return 'promotion';
  return 'note';
}

export interface Celebration {
  profileId: string;
  name: string;
  kind: 'birthday' | 'anniversary';
  /** Local calendar date of this occurrence, YYYY-MM-DD. */
  date: string;
  daysUntil: number;
  /** Completed years, for anniversaries. */
  years?: number;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoFromParts(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Next calendar occurrence of a month/day, including today. Feb 29 lands on Feb 28 in non-leap years. */
function nextOccurrence(month: number, day: number, today: Date): Date {
  const year = today.getFullYear();
  const safeDay = (y: number) => {
    const last = new Date(y, month, 0).getDate();
    return Math.min(day, last);
  };
  let occ = new Date(year, month - 1, safeDay(year));
  if (occ < startOfDay(today)) {
    occ = new Date(year + 1, month - 1, safeDay(year + 1));
  }
  return occ;
}

function daysBetween(from: Date, to: Date): number {
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / 86_400_000);
}

/**
 * Birthdays and work anniversaries inside the next `withinDays` days
 * (today included). Birthdays are omitted when the person has asked
 * not to be celebrated. Anniversaries require at least one completed year.
 */
export function upcomingCelebrations(
  people: {
    profileId: string;
    name: string;
    dateOfBirth: string | null;
    startDate: string | null;
    celebrateBirthday: boolean;
  }[],
  today = new Date(),
  withinDays = 14,
): Celebration[] {
  const out: Celebration[] = [];
  for (const person of people) {
    if (person.celebrateBirthday && person.dateOfBirth) {
      const [, m, d] = person.dateOfBirth.slice(0, 10).split('-').map(Number);
      if (m && d) {
        const occ = nextOccurrence(m, d, today);
        const daysUntil = daysBetween(today, occ);
        if (daysUntil <= withinDays) {
          out.push({
            profileId: person.profileId,
            name: person.name,
            kind: 'birthday',
            date: isoFromParts(occ.getFullYear(), occ.getMonth() + 1, occ.getDate()),
            daysUntil,
          });
        }
      }
    }
    if (person.startDate) {
      const [sy, m, d] = person.startDate.slice(0, 10).split('-').map(Number);
      if (sy && m && d) {
        const occ = nextOccurrence(m, d, today);
        const years = occ.getFullYear() - sy;
        const daysUntil = daysBetween(today, occ);
        if (years >= 1 && daysUntil <= withinDays) {
          out.push({
            profileId: person.profileId,
            name: person.name,
            kind: 'anniversary',
            date: isoFromParts(occ.getFullYear(), occ.getMonth() + 1, occ.getDate()),
            daysUntil,
            years,
          });
        }
      }
    }
  }
  return out.sort((a, b) => a.daysUntil - b.daysUntil || a.name.localeCompare(b.name));
}

export function celebrationPhrase(item: Celebration): string {
  const when =
    item.daysUntil === 0
      ? 'today'
      : item.daysUntil === 1
        ? 'tomorrow'
        : `in ${item.daysUntil} days`;
  if (item.kind === 'birthday') return `${item.name}'s birthday is ${when}`;
  const years = item.years ?? 0;
  return `${item.name}'s ${years}-year anniversary is ${when}`;
}

export function getTimezones(): string[] {
  type IntlWithTz = typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  const i18n = Intl as IntlWithTz;
  if (typeof i18n.supportedValuesOf === 'function') {
    try {
      return i18n.supportedValuesOf('timeZone');
    } catch {
      /* fall through */
    }
  }
  return [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'UTC',
  ];
}

export function friendlyHrError(err: unknown): string {
  const raw =
    err instanceof Error
      ? err.message
      : err && typeof err === 'object' && 'message' in err
        ? String((err as { message: unknown }).message)
        : '';
  if (raw.includes('hr_forbidden')) return 'You do not have permission to edit employee records.';
  if (raw.includes('hr_manager_self')) return 'Someone cannot be their own manager.';
  if (raw.includes('hr_manager_not_found')) return 'That manager is not in this company.';
  if (raw.includes('hr_employee_not_found')) return 'That person is not in this company.';
  if (raw.includes('hr_employment_type')) return 'Pick a valid employment type.';
  if (raw.includes('hr_employment_status')) return 'Pick a valid employment status.';
  return raw || 'Could not save.';
}
